import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { Arenda } from './api';
import {
  ostalos as ostalosSnimka,
  otkrytResurs,
  otmetitAktivnost as otmetitVReestre,
  perehvatit as perehvatitVReestre,
  podpisatsya,
  prodolzhit as prodolzhitVReestre,
  snimok,
  type SnimokArendy,
} from './reestr';
import { versiyaResursa } from '../../api/vkladka';

/**
 * ОДИН РЕДАКТОР КАРТОЧКИ (`s719`; слова владельца: «аренда при открытии, второй пользователь
 * только читает и видит имя редактора»).
 *
 * Хук зовут один раз на карточку (лид с перепиской — `'inquiry'`, запись — `'booking'`, бот —
 * `'bot'`): при открытии он сам берёт аренду, подаёт пульс, отпускает при закрытии и опрашивает
 * состояние, пока редактирует другой. Звать `vzyat()` не нужно. Несколько хуков одного ресурса на
 * одном экране делят одно состояние (см. `reestr.ts`).
 *
 * ⛔ ЗАЩИЩАЕТ СЕРВЕР. `tolkoChtenie` отключает поля, чтобы человек не набирал впустую; чужой ход
 * всё равно остановит страж сервера.
 */
export interface Redaktor {
  /** Аренда моя и жива: поля и кнопки живые. */
  mozhnoPravit: boolean;
  /** Обратное: карточка только читается (редактирует другой, аренда истекла или ещё не взята). */
  tolkoChtenie: boolean;
  /** Кто редактирует, если это не я: имя и с какого времени. */
  redaktor: { imya: string; s: string | null } | null;
  /** Версия ресурса, с которой работает экран (уходит с ходом заголовком `X-Versiya` сама). */
  versiya: string | null;
  /** Секунд до истечения моей аренды; `null` — аренда не моя. */
  ostalos: number | null;
  /** Пора предупредить: до истечения меньше порога сервера. */
  skoroIstechyot: boolean;
  /** Моя аренда истекла из-за бездействия, её ещё никто не взял: «Продолжить». */
  istekla: boolean;
  /** Редактирование свободно, а аренда не моя и сама не возьмётся: «Редактировать». */
  svobodno: boolean;
  /** Аренда была моей и ушла (перехват, истечение с новым редактором): вернуть её может только кнопка. */
  poteryana: boolean;
  /** Сервер дал право перехвата и редактирует другой. */
  mozhnoPerehvatit: boolean;
  /** Слово и код последнего отказа (перехвачено, утрачено, нет права). */
  slovo: string | null;
  kod: string | null;
  idyot: boolean;
  /** «Продолжить» / «Редактировать»: продлить свою аренду либо взять заново. */
  prodolzhit: () => Promise<void>;
  perehvatit: () => Promise<void>;
  /** Действие человека в карточке (ввод, выбор): продлевает аренду. Клавиши и клики реестр ловит сам. */
  otmetitAktivnost: () => void;
  arenda: Arenda | null;

  // Поля прежнего `useArenda` — чтобы плашка и старые места работали с тем же объектом.
  est: boolean;
  tolkoProsmotr: boolean;
  vzyat: (perenesti?: boolean) => Promise<boolean>;
}

export interface NastroykiRedaktora {
  /** Брать аренду при открытии (по умолчанию да). `false` — только показывать, кто редактирует. */
  brat?: boolean;
  /** Аренда из ответа самой карточки — чтобы не ждать первого запроса состояния. */
  sServera?: Arenda | null;
}

export function sobratRedaktora(
  s: SnimokArendy,
  versiya: string | null,
  brat: boolean,
  deystviya: Pick<Redaktor, 'prodolzhit' | 'perehvatit' | 'otmetitAktivnost'>,
): Redaktor {
  const a = s.arenda;
  const moya = a !== null && a.zanyata && a.moya;
  // Дверей аренды нет (прежний сервер) — экран работает, как раньше.
  const mozhnoPravit = !s.est ? brat : brat && moya;
  const ostalos = ostalosSnimka(s);
  const chuzhaya = a !== null && a.zanyata && !a.moya;

  return {
    mozhnoPravit,
    tolkoChtenie: !mozhnoPravit,
    redaktor: chuzhaya && a.vladelets !== null ? { imya: a.vladelets.imya, s: a.vzyata_display ?? null } : null,
    versiya,
    ostalos,
    skoroIstechyot: ostalos !== null && a !== null && ostalos <= a.pravila.preduprezhdenie_sek,
    istekla: a !== null && !a.zanyata && a.istekla,
    svobodno: brat && s.est && a !== null && !a.zanyata && !a.istekla && !s.idyot,
    poteryana: s.zhdyotRuki,
    mozhnoPerehvatit: chuzhaya && a.mozhno_perehvatit,
    slovo: s.otkaz?.slovo ?? null,
    kod: s.otkaz?.kod ?? null,
    idyot: s.idyot,
    ...deystviya,
    arenda: a,
    est: s.est && a !== null,
    tolkoProsmotr: s.est && !mozhnoPravit,
    vzyat: async () => {
      if (moya) {
        deystviya.otmetitAktivnost();

        return true;
      }

      return false;
    },
  };
}

export function useRedaktor(tip: string, id: number | undefined, nastroyki: NastroykiRedaktora = {}): Redaktor {
  const brat = nastroyki.brat ?? true;
  const sServera = nastroyki.sServera ?? null;

  useEffect(() => {
    if (id === undefined) return;

    return otkrytResurs(tip, id, brat, sServera);
    // Аренда из ответа карточки нужна только в миг открытия; дальше состояние ведёт реестр.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tip, id, brat]);

  const podpiska = useCallback((slushatel: () => void) => (id === undefined ? () => undefined : podpisatsya(tip, id, slushatel)), [tip, id]);
  const s = useSyncExternalStore(podpiska, () => (id === undefined ? snimok('', 0) : snimok(tip, id)));

  const deystviya = useMemo(
    () => ({
      prodolzhit: async () => {
        if (id !== undefined) await prodolzhitVReestre(tip, id);
      },
      perehvatit: async () => {
        if (id !== undefined) await perehvatitVReestre(tip, id);
      },
      otmetitAktivnost: () => {
        if (id !== undefined) otmetitVReestre(tip, id);
      },
    }),
    [tip, id],
  );

  return sobratRedaktora(s, id === undefined ? null : versiyaResursa(`${tip}:${id}`), brat, deystviya);
}
